/**
 * Selección determinista del paquete vigente (MVP-02B §6).
 *
 * Una misma versión fuente puede acumular varios intentos de paquete: uno
 * inválido, otro regenerado, otro aprobado. Tanto el propietario
 * (`DigitalPriceModal`) como la administración (`DigitalProductsAdmin`)
 * escogían con `find()` o `data[0]`, es decir, el primero que devolviera la
 * API. Con el orden por fecha eso es "el más reciente", que no es lo mismo que
 * "el vigente": si el último intento falló, el propietario perdía el botón de
 * carrito de un paquete que sí estaba aprobado.
 *
 * Los intentos antiguos no se borran nunca: la auditoría se conserva y aquí
 * solo se decide cuál se muestra.
 */

/** Mayor gana. Refleja lo avanzado del paquete en el flujo comercial. */
export const PACKAGE_STATE_PRIORITY = {
    aprobado: 5,
    pendiente_revision: 4,
    generando: 3,
    invalido: 2,
    rechazado: 1,
    obsoleto: 0,
};

export function pickCanonicalPackage(packages, sourceVersionId = null) {
    const candidatos = (packages || []).filter((candidato) =>
        sourceVersionId === null
            ? true
            : String(candidato.source_version_id) === String(sourceVersionId),
    );
    if (candidatos.length === 0) return null;

    return candidatos.reduce((mejor, actual) => {
        const prioridadMejor = PACKAGE_STATE_PRIORITY[mejor.state] ?? -1;
        const prioridadActual = PACKAGE_STATE_PRIORITY[actual.state] ?? -1;
        if (prioridadActual !== prioridadMejor) {
            return prioridadActual > prioridadMejor ? actual : mejor;
        }
        // Empate de estado: el id mayor es el intento más reciente. Desempata
        // de forma estable, porque dos intentos pueden compartir `created_at`.
        return Number(actual.id) > Number(mejor.id) ? actual : mejor;
    });
}

/** Recorrido comercial en lenguaje de usuario, nunca estados internos. */
export function buildOwnerJourney({ uiState, packageState, pkg }) {
    const priceCalculated = !["loading", "empty", "error"].includes(uiState);
    const priceApproved = uiState === "aprobado";
    const packageReady = ["pendiente_revision", "aprobado"].includes(packageState);
    const licenseAssigned = Boolean(pkg?.required_license_version);
    const readyToBuy = packageState === "aprobado";

    const paso = (done, current) => (done ? "done" : current ? "current" : "pending");

    return [
        {
            id: "precio-calculado",
            label: "Precio calculado",
            state: paso(priceCalculated, !priceCalculated),
            actor: priceCalculated ? undefined : "Calcula el precio para empezar.",
        },
        {
            id: "precio-aprobado",
            label: "Precio aprobado",
            state: paso(priceApproved, priceCalculated && !priceApproved),
            actor:
                priceCalculated && !priceApproved
                    ? "Administración está revisando el precio. No necesitas hacer nada."
                    : undefined,
        },
        {
            id: "paquete-preparado",
            label: "Paquete preparado",
            state: paso(packageReady, priceApproved && !packageReady),
            actor:
                priceApproved && !packageReady
                    ? "Administración está preparando los archivos."
                    : undefined,
        },
        {
            id: "licencia-asignada",
            label: "Licencia asignada",
            state: paso(licenseAssigned, packageReady && !licenseAssigned),
            actor:
                packageReady && !licenseAssigned
                    ? "Administración está asignando la licencia de uso."
                    : undefined,
        },
        {
            id: "listo-para-comprar",
            label: "Listo para comprar",
            state: paso(readyToBuy, licenseAssigned && !readyToBuy),
            description: readyToBuy ? "Ya puedes añadirlo al carrito." : undefined,
        },
    ];
}
